import React, { useState, useEffect } from 'react';
import { Search, X, ArrowRight, FileText, DollarSign, HelpCircle, Plane } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const SearchModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Searchable content
  const searchableContent = [
    // Services
    { type: 'service', title: 'Pet Relocation Services', description: 'Complete relocation assistance for pets moving to and from Cayman Islands', url: '/#services', icon: Plane },
    { type: 'service', title: 'Pet Import Services', description: 'Expert assistance for importing pets into the Cayman Islands', url: '/#services', icon: Plane },
    { type: 'service', title: 'Visiting Pet Services', description: 'Temporary pet travel assistance for tourists and seasonal residents', url: '/#services', icon: Plane },
    { type: 'service', title: 'Vacation Travel Assistance', description: 'Help for Cayman residents traveling abroad with pets', url: '/#services', icon: Plane },
    { type: 'service', title: 'Health Certificates', description: 'Veterinary health certificate preparation and verification', url: '/#services', icon: Plane },
    { type: 'service', title: 'Documentation Assistance', description: 'Complete documentation handling for pet travel', url: '/#services', icon: Plane },
    { type: 'service', title: 'Airline Coordination', description: 'Coordination with airlines for pet transportation', url: '/#services', icon: Plane },
    { type: 'service', title: 'TravelSafe Pet Readiness Service', description: 'Annual service keeping your pet travel-ready - CI$169/year', url: '/travelsafe', icon: Plane },
    
    // Pricing
    { type: 'pricing', title: 'Initial Consultation', description: 'Up to 30 minutes consultation - CI$50', url: '/#pricing', icon: DollarSign },
    { type: 'pricing', title: 'Import Permits', description: 'Assistance with import permit applications', url: '/#pricing', icon: DollarSign },
    { type: 'pricing', title: 'Export Permits', description: 'Export documentation and permit assistance', url: '/#pricing', icon: DollarSign },
    { type: 'pricing', title: 'Rabies Titre Tests', description: 'Blood test coordination and documentation', url: '/#pricing', icon: DollarSign },
    { type: 'pricing', title: 'Health Certificate Review', description: 'Veterinary health certificate verification', url: '/#pricing', icon: DollarSign },
    
    // Resources
    { type: 'resource', title: 'Import Checklist', description: 'Comprehensive checklist for importing pets to Cayman Islands', url: '/import-checklist', icon: FileText },
    { type: 'resource', title: 'How It Works', description: 'Step-by-step guide to our pet relocation process', url: '/#how-it-works', icon: FileText },
    { type: 'resource', title: 'FAQ', description: 'Frequently asked questions about pet travel', url: '/#faq', icon: HelpCircle },
    { type: 'resource', title: 'About Us', description: 'Learn about Dr. Samantha Dorman and Pawsome Ventures', url: '/#about', icon: FileText },
    
    // Common searches
    { type: 'topic', title: 'Microchipping', description: 'ISO-compliant microchip requirements for pet import', url: '/import-checklist', icon: FileText },
    { type: 'topic', title: 'Rabies Vaccination', description: 'Rabies vaccination requirements and timing', url: '/import-checklist', icon: FileText },
    { type: 'topic', title: 'FAVN Test', description: 'Rabies titre test requirements and approved laboratories', url: '/import-checklist', icon: FileText },
    { type: 'topic', title: 'Quarantine', description: 'Pet quarantine information and requirements', url: '/#faq', icon: HelpCircle },
    { type: 'topic', title: 'Airlines', description: 'Airline coordination and IATA requirements', url: '/#services', icon: Plane },
    { type: 'topic', title: 'Cayman Islands Import', description: 'Complete guide to importing pets into Cayman Islands', url: '/import-checklist', icon: FileText },
    { type: 'topic', title: 'Pet Export', description: 'Exporting pets from Cayman Islands', url: '/#services', icon: Plane },
  ];

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = searchableContent.filter(item => 
      item.title.toLowerCase().includes(query) || 
      item.description.toLowerCase().includes(query)
    );

    setResults(filtered.slice(0, 8)); // Limit to 8 results
    setActiveIndex(0);
  }, [searchQuery]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => (prev - 1 + results.length) % results.length);
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        handleResultClick(results[activeIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, activeIndex]);

  const handleResultClick = (result) => {
    navigate(result.url);
    onClose();
    setSearchQuery('');
  };

  const getTypeColor = (type) => {
    const colors = {
      service: 'bg-blue-100 text-blue-700',
      pricing: 'bg-green-100 text-green-700',
      resource: 'bg-purple-100 text-purple-700',
      topic: 'bg-orange-100 text-orange-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getTypeLabel = (type) => {
    const labels = {
      service: 'Service',
      pricing: 'Pricing',
      resource: 'Resource',
      topic: 'Topic'
    };
    return labels[type] || 'Content';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-start justify-center p-4 pt-20">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border-2 border-primary/20 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-border">
            <Search className="w-5 h-5 text-foreground/40 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search services, pricing, or resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-foreground placeholder:text-foreground/40 text-lg"
              autoFocus
            />
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-foreground/60" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {searchQuery.trim() === '' ? (
              <div className="p-8 text-center">
                <Search className="w-12 h-12 text-foreground/20 mx-auto mb-3" />
                <p className="text-foreground/60 text-sm">Start typing to search...</p>
                <div className="mt-6 flex flex-wrap gap-2 justify-center">
                  <span className="text-xs text-foreground/40">Try:</span>
                  {['Import', 'TravelSafe', 'Pricing', 'Microchip', 'Rabies'].map(term => (
                    <button
                      key={term}
                      onClick={() => setSearchQuery(term)}
                      className="text-xs px-3 py-1 bg-muted hover:bg-muted/80 rounded-full text-foreground/70 transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            ) : results.length > 0 ? (
              <div className="p-2">
                {results.map((result, index) => {
                  const Icon = result.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => handleResultClick(result)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full text-left p-4 rounded-xl transition-all flex items-start gap-4 group ${
                        index === activeIndex 
                          ? 'bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/30' 
                          : 'hover:bg-muted/50 border-2 border-transparent'
                      }`}
                    >
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center ${
                        index === activeIndex ? 'scale-110' : ''
                      } transition-transform`}>
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {result.title}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(result.type)}`}>
                            {getTypeLabel(result.type)}
                          </span>
                        </div>
                        <p className="text-sm text-foreground/60 line-clamp-1">
                          {result.description}
                        </p>
                      </div>
                      <ArrowRight className={`w-5 h-5 text-foreground/30 flex-shrink-0 transition-all ${
                        index === activeIndex ? 'translate-x-1 text-primary' : ''
                      }`} />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center">
                  <Search className="w-6 h-6 text-foreground/40" />
                </div>
                <p className="text-foreground/60">No results found for "{searchQuery}"</p>
                <p className="text-sm text-foreground/40 mt-2">Try searching for services, pricing, or resources</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 bg-muted/30 border-t border-border flex items-center justify-between text-xs text-foreground/50">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white rounded border border-border">↑</kbd>
                <kbd className="px-2 py-1 bg-white rounded border border-border">↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white rounded border border-border">Enter</kbd>
                Select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white rounded border border-border">Esc</kbd>
              Close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
